import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CategoriaService } from '../../core/services/categoria.service';
import { ImagenService } from '../../core/services/imagen.service';
import { AlertService } from '../../core/services/alert.service';
import { CategoriaInventaApi } from '../../core/models/api/categoria-api';
import { PhotoGalleryEditorComponent, PhotoSlot } from '../../shared/components/photo-gallery-editor.component';
import { UiLoadingComponent } from '../../shared/components/ui-loading.component';
import { SafeImageComponent } from '../../shared/components/safe-image.component';

@Component({
    selector: 'app-admin-categorias',
    standalone: true,
    imports: [CommonModule, FormsModule, PhotoGalleryEditorComponent, UiLoadingComponent, SafeImageComponent],
    template: `
    <div class="mb-6">
      <h2 class="text-2xl font-black text-gray-800">Categorías</h2>
      <p class="text-sm text-gray-400 mt-1">Vienen de inventa. Actívalas para mostrarlas en la web y luego configura su foto, orden y anidado. Puedes configurarlas antes o después de activarlas.</p>
    </div>

    <app-ui-loading *ngIf="loading()"></app-ui-loading>

    <div *ngIf="!loading()" class="grid grid-cols-1 lg:grid-cols-12 gap-6">
      <form *ngIf="formVisible()" class="lg:col-span-5 bg-white rounded-[2rem] p-6 border border-rose-100 shadow-sm flex flex-col gap-4 h-fit" (ngSubmit)="guardar()">
        <p class="text-sm font-black text-gray-800">Configurar categoría</p>

        <label class="block">
          <span class="text-xs font-bold text-gray-500 uppercase tracking-wide">Nombre (inventa)</span>
          <input [ngModel]="nombre()" name="nombre" readonly class="mt-1 w-full rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3 text-sm text-gray-500 outline-none" />
        </label>

        <app-photo-gallery-editor
          [photos]="fotos()"
          (photosChange)="fotos.set($event)"
          [multiple]="false"
          title="Foto"
          hint="Se muestra primero y se sube al guardar.">
        </app-photo-gallery-editor>

        <label class="block">
          <span class="text-xs font-bold text-gray-500 uppercase tracking-wide">Slug (web)</span>
          <input [ngModel]="slug()" (ngModelChange)="slug.set($event)" name="slug" placeholder="Se genera desde el nombre si lo dejas vacío" class="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-rose-200" />
        </label>
        <label class="block">
          <span class="text-xs font-bold text-gray-500 uppercase tracking-wide">Categoría padre</span>
          <select [ngModel]="padreUuid()" (ngModelChange)="padreUuid.set($event)" name="padreUuid" class="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-rose-200 bg-white">
            <option [ngValue]="null">Sin padre (menú principal)</option>
            <option *ngFor="let c of padresDisponibles()" [ngValue]="c.uuid">{{ c.nombre }}</option>
          </select>
        </label>
        <label class="block">
          <span class="text-xs font-bold text-gray-500 uppercase tracking-wide">Descripción (web)</span>
          <textarea [ngModel]="descripcion()" (ngModelChange)="descripcion.set($event)" name="descripcion" rows="2" class="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-rose-200"></textarea>
        </label>
        <label class="block">
          <span class="text-xs font-bold text-gray-500 uppercase tracking-wide">Orden</span>
          <input [ngModel]="orden()" (ngModelChange)="orden.set($event)" name="orden" type="number" class="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-rose-200" />
          <span class="text-[11px] text-gray-400">Menor número aparece primero en el menú y en el catálogo.</span>
        </label>
        <label class="flex items-center gap-2 text-sm text-gray-700 font-medium">
          <input type="checkbox" [ngModel]="mostrarEnMenu()" (ngModelChange)="mostrarEnMenu.set($event)" name="mostrarEnMenu" class="accent-rose-500" />
          Mostrar en el menú
        </label>

        <div class="flex gap-2">
          <button class="flex-1 py-3 rounded-2xl text-white font-bold text-sm" style="background-color: var(--bg-main);">Guardar configuración</button>
          <button type="button" (click)="cancelar()" class="px-4 py-3 rounded-2xl text-sm font-bold text-gray-500 border border-gray-200">Cancelar</button>
        </div>
      </form>

      <div class="bg-white rounded-[2rem] border border-rose-100 overflow-hidden shadow-sm" [ngClass]="formVisible() ? 'lg:col-span-7' : 'lg:col-span-12'">
        <p class="px-4 pt-4 pb-2 text-xs font-bold text-gray-500 uppercase tracking-wide">Categorías de inventa</p>
        <div *ngFor="let c of inventa()" class="flex items-center gap-3 px-4 py-3 border-b border-gray-50 last:border-0 hover:bg-rose-50/40">
          <div class="w-12 h-12 rounded-xl overflow-hidden bg-[#fff5f7] shrink-0">
            <app-safe-image [src]="c.urlImagen" [alt]="c.nombre" iconClass="w-5 h-5" imgClass="w-full h-full object-cover"></app-safe-image>
          </div>
          <div class="min-w-0 flex-1">
            <div class="flex items-center gap-2">
              <p class="font-bold text-gray-800 truncate">{{ c.nombre }}</p>
              <span *ngIf="c.configurada" class="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-600">Activada</span>
              <span *ngIf="!c.configurada" class="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-50 text-amber-600">Sin activar</span>
            </div>
            <p class="text-xs text-gray-400">{{ c.cantidadProductos }} productos activos<span *ngIf="c.slug"> · {{ c.slug }}</span></p>
          </div>
          <div class="flex items-center gap-3 text-xs font-bold shrink-0">
            <button *ngIf="!c.configurada" type="button" (click)="activar(c)" class="text-rose-500 hover:underline cursor-pointer">Activar en la web</button>
            <ng-container *ngIf="c.configurada">
              <button type="button" (click)="editar(c)" class="text-rose-500 hover:underline cursor-pointer">Configurar</button>
              <ng-container *ngIf="desactivando() !== c.uuid">
                <button type="button" (click)="desactivando.set(c.uuid)" class="text-gray-400 hover:text-red-500 cursor-pointer">Quitar de la web</button>
              </ng-container>
              <ng-container *ngIf="desactivando() === c.uuid">
                <span class="text-red-500">¿Quitar de la web?</span>
                <button type="button" (click)="desactivar(c)" class="text-red-500 hover:underline cursor-pointer">Sí, quitar</button>
                <button type="button" (click)="desactivando.set(null)" class="text-gray-400 hover:underline cursor-pointer">Cancelar</button>
              </ng-container>
            </ng-container>
          </div>
        </div>
        <p *ngIf="inventa().length === 0" class="px-4 py-8 text-center text-sm text-gray-400">No hay categorías en inventa todavía.</p>
      </div>
    </div>
    `
})
export class AdminCategoriasComponent implements OnInit {
    private categoriaService = inject(CategoriaService);
    private imagenService = inject(ImagenService);
    private alerts = inject(AlertService);

    inventa = signal<CategoriaInventaApi[]>([]);
    padresDisponibles = computed(() => this.inventa().filter((c) => c.configurada && c.uuid && c.uuid !== this.editUuid()));

    editUuid = signal<string | null>(null);
    inventaCategoriaId = signal<number | null>(null);
    nombre = signal('');
    slug = signal('');
    padreUuid = signal<string | null>(null);
    descripcion = signal('');
    orden = signal(0);
    mostrarEnMenu = signal(true);
    imagenUuid = signal<string | null>(null);
    fotos = signal<PhotoSlot[]>([]);
    loading = signal(true);

    desactivando = signal<string | null>(null);
    formVisible = computed(() => this.inventaCategoriaId() !== null);

    ngOnInit(): void {
        this.reload();
    }

    reload() {
        this.loading.set(true);
        this.categoriaService.listCategoriasInventa().subscribe({
            next: (inventa) => {
                this.inventa.set(inventa);
                this.loading.set(false);
            },
            error: () => {
                this.loading.set(false);
                this.alerts.error('No se pudieron cargar las categorías.');
            }
        });
    }

    editar(c: CategoriaInventaApi) {
        this.editUuid.set(c.uuid);
        this.inventaCategoriaId.set(c.inventaCategoriaId);
        this.nombre.set(c.nombre);
        this.slug.set(c.slug ?? '');
        this.padreUuid.set(c.padreUuid);
        this.descripcion.set(c.descripcion ?? '');
        this.orden.set(c.orden);
        this.mostrarEnMenu.set(c.mostrarEnMenu);
        this.imagenUuid.set(c.imagenUuid);
        this.fotos.set(c.urlImagen ? [{ previewUrl: c.urlImagen, imagenUuid: c.imagenUuid, file: null }] : []);
    }

    cancelar() {
        this.editUuid.set(null);
        this.inventaCategoriaId.set(null);
        this.nombre.set('');
        this.slug.set('');
        this.padreUuid.set(null);
        this.descripcion.set('');
        this.orden.set(0);
        this.mostrarEnMenu.set(true);
        this.imagenUuid.set(null);
        this.fotos.set([]);
    }

    activar(c: CategoriaInventaApi) {
        this.categoriaService.activarCategoria(c.inventaCategoriaId).subscribe({
            next: (ok) => {
                if (ok) {
                    this.alerts.ok('Categoría activada para web.');
                    this.reload();
                } else {
                    this.alerts.error('No se pudo activar la categoría.');
                }
            },
            error: () => this.alerts.error('No se pudo activar la categoría.')
        });
    }

    desactivar(c: CategoriaInventaApi) {
        this.desactivando.set(null);
        this.categoriaService.desactivarCategoria(c.inventaCategoriaId).subscribe({
            next: (ok) => {
                if (ok) {
                    this.alerts.ok('Categoría desactivada de la web.');
                    if (this.inventaCategoriaId() === c.inventaCategoriaId) {
                        this.cancelar();
                    }
                    this.reload();
                } else {
                    this.alerts.error('No se pudo desactivar la categoría.');
                }
            },
            error: () => this.alerts.error('No se pudo desactivar la categoría.')
        });
    }

    guardar() {
        const pending = this.fotos().filter((f) => !!f.file).map((f) => f.file as File);
        if (pending.length) {
            this.imagenService.upload(pending).subscribe({
                next: (imgs) => this.enviar(imgs[0]?.uuid ?? this.imagenUuid()),
                error: (err) => this.alerts.error(err?.message || 'No se pudo subir la foto')
            });
            return;
        }
        this.enviar(this.fotos()[0]?.imagenUuid ?? null);
    }

    private enviar(imagenUuid: string | null) {
        const inventaCategoriaId = this.inventaCategoriaId();
        if (!inventaCategoriaId) {
            this.alerts.error('Selecciona una categoría.');
            return;
        }

        const body = {
            uuid: this.editUuid(),
            inventaCategoriaId,
            padreUuid: this.padreUuid(),
            slug: this.slug() || undefined,
            descripcion: this.descripcion() || undefined,
            imagenUuid,
            mostrarEnMenu: this.mostrarEnMenu(),
            orden: Number(this.orden()) || 0
        };

        this.categoriaService.configCategoria(body).subscribe({
            next: (ok) => {
                if (ok) {
                    this.alerts.ok('Categoría guardada');
                    this.cancelar();
                    this.reload();
                } else {
                    this.alerts.error('No se pudo guardar');
                }
            },
            error: () => this.alerts.error('No se pudo guardar')
        });
    }
}
